import { useEffect, type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'

import { Icon } from '@/icons/Icon'
import { LogoMark } from '@/components/Logo'
import { useNavState } from '@/lib/navState'

import { usePlatformAuth } from './auth'

/**
 * The portal's chrome, drawn with the application's own.
 *
 * `ix-app`, `ix-sidebar`, `ix-topbar`, `ix-page` and the `ix-nav-item` rail are
 * the product's shell (`layouts/AppLayout.tsx`), and the portal uses them
 * rather than inventing a second one: somebody who administers the platform
 * also uses the application, and two different-looking products is a cost paid
 * by the only people who ever see both.
 *
 * What is separate is everything behind the chrome - the session, the
 * principal, the endpoints, and the fact that no carrier's user can reach any
 * of it. The one visual difference is the word under the mark, because "which
 * carrier am I in" and "I am above all of them" are not a distinction to leave
 * to memory.
 */

const NAV = [
  { to: '/admin/organizations', label: 'Organizations', icon: 'layout-grid' },
  { to: '/admin/administrators', label: 'Administrators', icon: 'shield' },
  { to: '/admin/activity', label: 'Activity', icon: 'clock' },
] as const

function navClass({ isActive }: { isActive: boolean }) {
  return isActive ? 'ix-nav-item is-active' : 'ix-nav-item'
}

export function PlatformLayout({ title, children }: { title: string; children: ReactNode }) {
  const { expanded, toggle, close } = useNavState()
  const { me, signOut } = usePlatformAuth()

  useEffect(() => {
    document.title = `${title} · Insuronix Platform`
  }, [title])

  const initials = (me?.admin.fullName || me?.admin.email || '?')
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <div className="ix-app">
      <div className="ix-scrim" onClick={close} />

      <aside className="ix-sidebar">
        <NavLink className="ix-sidebar__brand" to="/admin/organizations" aria-label="Insuronix platform">
          <LogoMark variant="sidebar" />
        </NavLink>

        {/*
          The one place the portal says it is not the application, and it has
          to say it without shouting: this sits under the mark on every screen,
          so a solid block of colour here is the loudest thing in the product
          and it reports nothing that ever changes. A rule, a word, and the
          host it is serving.
        */}
        <div className="pf-scope">
          <span className="pf-scope__k">Platform</span>
          <span className="pf-scope__v" title={window.location.host}>
            {window.location.host}
          </span>
        </div>

        <div className="ix-sidebar__scroll">
          <nav className="ix-sidebar__group">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} className={navClass}>
                <Icon name={item.icon} size={16} />
                <span className="ix-nav-item__label">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="ix-sidebar__footer">
          <div className="ix-avatar">{initials}</div>
          <div className="ix-sidebar__user">
            <div className="ix-sidebar__user-name">{me?.admin.fullName ?? ''}</div>
            <div className="ix-sidebar__user-links">
              <NavLink to="/admin/password" style={{ color: 'var(--ix-primary)' }}>
                Password
              </NavLink>
            </div>
          </div>
          <a
            className="ix-signout"
            href="/admin/login"
            title="Sign out"
            aria-label="Sign out"
            onClick={(e) => {
              e.preventDefault()
              void signOut()
            }}
          >
            <Icon name="arrow-up-from-line" size={16} />
          </a>
        </div>
      </aside>

      <div className="ix-main">
        <header className="ix-topbar">
          <div className="ix-topbar__left">
            <button
              className="ix-topbar__menu"
              type="button"
              aria-label="Toggle navigation"
              aria-expanded={expanded}
              onClick={toggle}
              style={{
                background: 'none',
                border: 0,
                padding: 0,
                cursor: 'pointer',
                color: 'inherit',
                display: 'inline-flex',
              }}
            >
              <Icon name="menu" size={18} />
            </button>
            <h1 className="ix-topbar__title">{title}</h1>
          </div>
          <div className="ix-topbar__right">
            <span className="pf-whoami" title={me?.admin.email}>
              <span className="pf-whoami__dot" aria-hidden="true" />
              <span className="pf-whoami__e">{me?.admin.email}</span>
            </span>
          </div>
        </header>
        <div className="ix-page">{children}</div>
      </div>
    </div>
  )
}

export default PlatformLayout
